import { createContext } from "react";
import { IUser } from "../utils/UserInterfaces";
import { IReservation } from "../utils/ReservationInterfaces";
import {
    defaultUserValue,
    reservationDefaultValue,
    searchDefaultValue,
} from "../utils/defaultNullValues";

// Import blog-related interfaces
import { IArticleCategory } from "../utils/ArticleInterface";
import { IPartner } from "../utils/ArticleInterface";

// Define interfaces for BlogContext
export interface IBlogData {
  categories: IArticleCategory[];
  partners: IPartner[];
  isLoading: boolean;
  lastUpdated: number;
  currentLanguage: string;
}

export interface IBlogContext {
  blogData: IBlogData;
  setBlogData: (data: IBlogData) => void;
  refreshBlogData: (language?: string) => Promise<void>; // Make refresh function accept language
  setLanguage: (language: string) => void; // Add function to change language
}

// Default values for BlogContext
export const defaultBlogData: IBlogData = {
  categories: [],
  partners: [],
  isLoading: false,
  lastUpdated: 0,
  currentLanguage: 'fr', // Default to French
};

const defaultBlogContext: IBlogContext = {
  blogData: defaultBlogData,
  setBlogData: () => {},
  refreshBlogData: async () => {},
  setLanguage: () => {},
};

export interface IFooterData {
  links: any;
  isLoading: boolean;
  lastUpdated: number;
}

export interface IFooterContext {
  footerData: IFooterData;
  setFooterData: (data: IFooterData) => void;
  refreshFooterData: () => Promise<void>;
}

export const defaultFooterData: IFooterData = {
  links: {},
  isLoading: false,
  lastUpdated: 0,
};

const defaultFooterContext: IFooterContext = {
  footerData: defaultFooterData,
  setFooterData: () => {},
  refreshFooterData: async () => {},
};

const UserContext = createContext<{
    user: IUser;
    setUser: (prop: IUser) => void;
}>({
    user: defaultUserValue,
    setUser: (prop) => {},
});

const ReservationContext = createContext<{
    reservation: IReservation;
    setReservation: (prop: IReservation) => void;
}>({
    reservation: reservationDefaultValue,
    setReservation: (prop) => {},
});
const SearchContext = createContext<{
    searchDta: any;
    setSearchData: (prop: any) => void;
}>({
    searchDta: searchDefaultValue,
    setSearchData: (prop) => {},
});
const CurrencyContext = createContext<{
    currency: string;
    setCurrency: (prop: any) => void;
}>({
    currency: "xaf",
    setCurrency: (prop) => {},
});

const CurrencyBaseContext = createContext<{
    currencyBase: string;
    setCurrencyBase: (prop: any) => void;
}>({
    currencyBase: "XAF",
    setCurrencyBase: (prop) => {},
});

const LoadingMenuContext = createContext<{
    loadingMenu: boolean;
    setLoadingMenu: (prop: any) => void;
}>({
    loadingMenu: false,
    setLoadingMenu: (prop) => {},
});

const BlogContext = createContext<IBlogContext>(defaultBlogContext);

const FooterContext = createContext<IFooterContext>(defaultFooterContext);

export {
    UserContext,
    ReservationContext,
    SearchContext,
    CurrencyContext,
    CurrencyBaseContext,
    LoadingMenuContext,
    BlogContext,
    FooterContext
};
    
